/**
 * LoginContextChoiceModal — Shown to staff/admin/manager users immediately
 * after they sign in via the storefront login form. Forces an explicit
 * choice between continuing as a customer (storefront) or going to the
 * admin panel — preventing accidental admin context leakage on the
 * customer-facing site.
 *
 * The choice is recorded in roleContextStore (per user_id) so the storefront
 * knows how to treat the session on subsequent loads.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShoppingBag, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { setRoleContext } from "@/stores/roleContextStore";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  open: boolean;
  userId: string | null;
  /** Role of the just-signed-in user — drives the copy. */
  role?: string | null;
  /** Called after the user makes a choice (so the parent can close/cleanup). */
  onChosen?: (choice: "customer" | "admin") => void;
}

/** Friendly label + description for each role. Staff intentionally do NOT
 *  see "manage" wording — they don't have full management privileges. */
function roleCopy(role: string | null | undefined): {
  title: string;
  description: string;
  adminLabel: string;
  adminHint: string;
} {
  switch (role) {
    case "super_admin":
      return {
        title: "Welcome back, Super Admin",
        description:
          "You're signed in as a Super Admin. Continue as a customer on the storefront, or jump straight into the admin dashboard.",
        adminLabel: "Go to Admin Panel",
        adminHint: "Full access to devices, orders, staff, settings, and oversight tools.",
      };
    case "admin":
      return {
        title: "Welcome back, Admin",
        description:
          "You're signed in as an Admin. Continue as a customer on the storefront, or open the admin dashboard to manage operations.",
        adminLabel: "Go to Admin Panel",
        adminHint: "Manage devices, orders, customers and content.",
      };
    case "manager":
      return {
        title: "Welcome back, Manager",
        description:
          "You're signed in as a Manager. Continue as a customer on the storefront, or open the admin dashboard to manage your team's work.",
        adminLabel: "Go to Manager Panel",
        adminHint: "Manage orders, customers, content and your team.",
      };
    case "staff":
    default:
      return {
        title: "Welcome back",
        description:
          "You're signed in as a Staff member. Continue as a customer on the storefront, or open your staff workspace.",
        adminLabel: "Open Staff Workspace",
        adminHint: "Access your assigned orders, enquiries and chat tools.",
      };
  }
}

/**
 * Ensure the staff/admin user also has a row in `customers` so that storefront
 * features (favorites, cart, orders) work without RLS rejection. Silent no-op
 * on failure — the storefront still works, just without persistence.
 */
async function ensureCustomerProfile(userId: string) {
  try {
    const { data: existing } = await supabase
      .from("customers")
      .select("user_id")
      .eq("user_id", userId)
      .maybeSingle();
    if (existing) return;
    const { data: userRes } = await supabase.auth.getUser();
    const u = userRes?.user;
    if (!u) return;
    await supabase.from("customers").insert({
      user_id: userId,
      email: u.email || "",
      full_name: (u.user_metadata as any)?.full_name || (u.user_metadata as any)?.username || null,
      phone_number: (u.user_metadata as any)?.phone_number || null,
    });
  } catch (e) {
    console.warn("ensureCustomerProfile failed (safe to ignore):", e);
  }
}

export default function LoginContextChoiceModal({ open, userId, role, onChosen }: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<"customer" | "admin" | null>(null);
  const copy = roleCopy(role);

  // Defensive: clear busy state when reopened.
  useEffect(() => { if (!open) setBusy(null); }, [open]);

  /** Build the storefront URL using the staff member's referral handle if available. */
  async function buildStorefrontHref(uid: string): Promise<string> {
    try {
      const { data } = await supabase
        .from("profiles")
        .select("username")
        .eq("user_id", uid)
        .maybeSingle();
      const ref = (data?.username || "").replace(/\s+/g, "");
      return ref ? `/?ref=${encodeURIComponent(ref)}` : "/";
    } catch {
      return "/";
    }
  }

  const handleCustomer = async () => {
    if (!userId) return;
    setBusy("customer");
    setRoleContext(userId, "customer");
    await ensureCustomerProfile(userId);
    const href = await buildStorefrontHref(userId);
    onChosen?.("customer");
    // Navigate to the staff member's own referral storefront (item 11).
    navigate(href);
  };

  const handleAdmin = () => {
    if (!userId) return;
    setBusy("admin");
    setRoleContext(userId, "admin");
    onChosen?.("admin");
    navigate("/admin");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onChosen?.("customer" as any); }}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:max-w-md p-4 sm:p-6 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">{copy.title}</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">{copy.description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 mt-2">
          <Button
            variant="default"
            className="h-auto min-h-[3.5rem] py-3 px-3 justify-start gap-3 whitespace-normal text-left"
            onClick={handleCustomer}
            disabled={busy !== null}
          >
            <ShoppingBag className="w-5 h-5 shrink-0" />
            <span className="flex-1 min-w-0 text-left">
              <span className="block font-bold text-sm break-words">Continue as Customer</span>
              <span className="block text-[11px] opacity-80 font-normal break-words leading-snug">Browse, favorite, and shop the storefront.</span>
            </span>
          </Button>
          <Button
            variant="outline"
            className="h-auto min-h-[3.5rem] py-3 px-3 justify-start gap-3 whitespace-normal text-left"
            onClick={handleAdmin}
            disabled={busy !== null}
          >
            <ShieldCheck className="w-5 h-5 shrink-0" />
            <span className="flex-1 min-w-0 text-left">
              <span className="block font-bold text-sm break-words">{copy.adminLabel}</span>
              <span className="block text-[11px] opacity-70 font-normal break-words leading-snug">{copy.adminHint}</span>
            </span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
